import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, Bell, ChevronDown, Building2 } from "lucide-react";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar";
import { useIsMobile } from "@/hooks/use-mobile";
import { useAuth } from "@/hooks/useAuth";
import * as Sentry from '@sentry/react';
import { toast } from "sonner";
import OrganizationSelector from "./OrganizationSelector";
import { useOrganization } from "@/hooks/useOrganization";
import ReleaseNotesModal from "./ReleaseNotesModal";

const Header = () => {
  const isMobile = useIsMobile();
  const [isSidebarOpen, setIsSidebarOpen] = useState(false);
  const [isReleaseNotesOpen, setReleaseNotesOpen] = useState(false);
  const { user, signOut } = useAuth();
  const { setOrganization } = useOrganization();
  const navigate = useNavigate();

  const handleSignOut = async () => {
    try {
      await signOut();
      toast.success("Logged out successfully");
      navigate("/login");
    } catch (error) {
      Sentry.captureException(error);
      toast.error("Error signing out");
    }
  };

  const getUserInitials = () => {
    if (user?.firstName && user?.lastName) {
      return `${user.firstName.charAt(0)}${user.lastName.charAt(
        0
      )}`.toUpperCase();
    }
    if (user?.email) {
      return user.email.charAt(0).toUpperCase();
    }
    return "U";
  };

  const getUserDisplayName = () => {
    if (user?.firstName) {
      return user.firstName;
    }
    return user?.email?.split("@")[0] || "User";
  };

  const isAdmin = user?.role === "admin";

  return (
    <header className="bg-white border-b border-border sticky top-0 z-10">
      <div className="container mx-auto px-4 py-3 flex justify-between items-center">
        {/* Left */}
        <div className="flex items-center gap-4">
          {isMobile && (
            <Button
              variant="ghost"
              size="icon"
              onClick={() => setIsSidebarOpen(!isSidebarOpen)}
              aria-label="Toggle sidebar"
            >
              <Menu className="h-5 w-5" />
            </Button>
          )}
          <Link to="/dashboard" className="flex items-center">
            <span className="text-xl font-bold mr-1">Tastermonial</span>
            <span className="text-xl font-bold text-glyco-purple">Lab</span>
          </Link>
        </div>

        {/* Right */}
        <div className="flex items-center gap-3">
          {/* Notifications, hidden until implemented */}
          {/* <Button variant="ghost" size="icon">
            <Bell className="h-5 w-5" />
          </Button> */}

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button variant="ghost" className="flex items-center gap-2">
                <Avatar className="h-8 w-8">
                  <AvatarImage src="" />
                  <AvatarFallback className="bg-glyco-purple text-white">
                    {getUserInitials()}
                  </AvatarFallback>
                </Avatar>
                {!isMobile && (
                  <>
                    <span>{getUserDisplayName()}</span>
                    <ChevronDown className="h-4 w-4" />
                  </>
                )}
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuLabel>My Account</DropdownMenuLabel>
              <DropdownMenuSeparator />
              <DropdownMenuItem onClick={() => navigate("/dashboard/settings")}>
                Settings
              </DropdownMenuItem>
              <DropdownMenuItem>
                <Link to="/dashboard/billing">Billing & Subscription</Link>
              </DropdownMenuItem>
              <DropdownMenuItem onClick={() => setReleaseNotesOpen(true)}>
                Release notes
              </DropdownMenuItem>
              <DropdownMenuSeparator />
              {isAdmin && (
                <>
                  <DropdownMenuItem onClick={() => setOrganization(null)}>
                    Switch Organization
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/dashboard/organizations")}
                  >
                    Manage Organizations
                  </DropdownMenuItem>
                  <DropdownMenuItem
                    onClick={() => navigate("/dashboard/product-upload")}
                  >
                    Product Upload
                  </DropdownMenuItem>
                  <DropdownMenuSeparator />
                </>
              )}
              <DropdownMenuItem onClick={handleSignOut}>
                Sign out
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>

          <ReleaseNotesModal open={isReleaseNotesOpen} onOpenChange={setReleaseNotesOpen} />
        </div>
      </div>
    </header>
  );
};

export default Header;
